import intersection from 'lodash/intersection';

import { SCHEMA_TYPE_ENUM, SCHEMA_TYPE_MULTI_ENUM } from '../../util/types';
import { createResourceLocatorString } from '../../util/routes';
import { parseSelectFilterOptions, constructQueryParamName } from '../../util/search';
import { parse, stringify } from '../../util/urlHelpers';
import { userDisplayNameAsString } from '../../util/data';
import { isFieldForCategory } from '../../util/fieldHelpers'; // Import for category filtering

/**
 * Validates URL parameters for nested category data.
 *
 * @param {String} prefix - The prefix for the category parameter (e.g., 'pub_category').
 * @param {Array<Object>} categories - List of categories to validate against.
 * @param {Number} level - The current nesting level (e.g., 1 for top-level categories).
 * @param {Object} params - The URL parameters to validate.
 * @returns {Object} Validated category parameters.
 */
const validURLParamForCategoryData = (prefix, categories, level, params) => {
  const levelKey = constructQueryParamName(`${prefix}${level}`, 'public');
  const levelValue = params?.[levelKey];
  const foundCategory = categories.find(cat => cat.id === params?.[levelKey]);
  const subcategories = foundCategory?.subcategories || [];
  return foundCategory && subcategories.length > 0
    ? {
        [levelKey]: levelValue,
        ...validURLParamForCategoryData(prefix, subcategories, level + 1, params),
      }
    : foundCategory
    ? { [levelKey]: levelValue }
    : {};
};

/**
 * Omits user field parameters that are not allowed with the current category selection.
 * Only keeps search parameters that have a matching configuration in userFieldsConfig,
 * but always includes parameters defined in defaultFiltersConfig.
 *
 * @param {Object} searchParams - Current search query parameters.
 * @param {Object[]} userFieldsConfig - Configuration containing user field filters.
 * @param {Object[]} defaultFiltersConfig - Default filter configurations to include by default.
 * @param {Object[]} listingCategories - Categories for category filtering.
 * @returns {Object} Search parameters excluding restricted user fields, but always including allowed defaults.
 */
export const omitLimitedUserFieldParams = (
  searchParams,
  userFieldsConfig,
  defaultFiltersConfig,
  listingCategories
) => {
  // Get all query param names from defaultFilterKeys
  const defaultFilterParamNames = defaultFiltersConfig.map(f =>
    constructQueryParamName(f.key, f.scope)
  );

  // Handle category filtering
  const categorySearchConfig = defaultFiltersConfig.find(f => f.schemaType === 'category');
  const validNestedCategoryParamNames = categorySearchConfig
    ? validURLParamForCategoryData(categorySearchConfig.key, listingCategories, 1, searchParams)
    : {};

  const result = Object.entries(searchParams).reduce(
    (picked, [searchParamKey, searchParamValue]) => {
      // Include if it exists in defaultFilterKeys
      if (defaultFilterParamNames.includes(searchParamKey)) {
        return { ...picked, [searchParamKey]: searchParamValue };
      }

      // Include if it matches a user field configuration
      const foundConfig = userFieldsConfig.find(
        f => constructQueryParamName(f.key, f.scope) === searchParamKey
      );

      // Check if the field is allowed for the current category
      const currentCategories = Object.values(validNestedCategoryParamNames);
      const isForCategory = isFieldForCategory(currentCategories, foundConfig);

      return foundConfig && isForCategory
        ? { ...picked, [searchParamKey]: searchParamValue }
        : picked;
    },
    {}
  );

  return { ...result, ...validNestedCategoryParamNames };
};

/**
 * Validates a filter search parameter against user extended data and default filter configurations.
 * Retains only valid parameter names and values as per the schema, discarding invalid ones.
 *
 * @param {String} queryParamName - The name of the search parameter.
 * @param {String|Number} paramValueRaw - The raw value of the search parameter.
 * @param {Array<Object>} userFieldFilters - Configuration for user's extended data filters.
 * @param {Array<Object>} defaultFiltersConfig - Default filter configurations to validate against.
 * @returns {Object} - A validated search parameter object or an empty object if invalid.
 */
export const validURLParamForExtendedData = (
  queryParamName,
  paramValueRaw,
  userFieldFilters,
  defaultFiltersConfig
) => {
  const paramValue = paramValueRaw.toString();

  // Retrieve the relevant filter configuration for the given query parameter name
  const userFieldFilterConfig = userFieldFilters.find(
    field => queryParamName === constructQueryParamName(field.key, field.scope)
  );
  const defaultFilterConfig = defaultFiltersConfig.find(
    field => queryParamName === constructQueryParamName(field.key, field.scope)
  );

  // Handle user extended data filters
  if (userFieldFilterConfig) {
    const { schemaType, enumOptions = [], filterConfig } = userFieldFilterConfig;
    const isSchemaTypeMultiEnum = schemaType === SCHEMA_TYPE_MULTI_ENUM;
    const searchMode = filterConfig?.searchMode;

    if ([SCHEMA_TYPE_ENUM, SCHEMA_TYPE_MULTI_ENUM].includes(schemaType)) {
      // Process ENUM or MULTI_ENUM schema types
      const valueArray = parseSelectFilterOptions(paramValue);
      const allowedValues = enumOptions.map(option => `${option.option}`);
      const validValues = intersection(valueArray, allowedValues).join(',');

      // Return validated values or an empty object if none are valid
      return validValues.length > 0
        ? {
            [queryParamName]:
              isSchemaTypeMultiEnum && searchMode
                ? `${searchMode}:${validValues}` // Apply search mode for multi-enum
                : validValues,
          }
        : {};
    }

    // For other schema types, return the parameter if non-empty
    return paramValue.length > 0 ? { [queryParamName]: paramValue } : {};
  }

  // Handle default filter configurations
  if (defaultFilterConfig) {
    return paramValue.length > 0 ? { [queryParamName]: paramValue } : {};
  }

  // Return empty object if no valid configuration is found
  return {};
};

/**
 * Validates and filters search query parameters.
 * Keeps only those parameters that match the configured user fields or default filters.
 *
 * @param {Object} params - Search query parameters from the URL.
 * @param {Array<Object>} userFieldsConfig - Configuration for user field filters.
 * @param {Array<Object>} defaultFiltersConfig - Default filter configurations for validation.
 * @param {Array<Object>} listingCategories - Categories for category filtering.
 * @param {boolean} dropNonFilterParams - If true, removes parameters not related to filters.
 * @returns {Object} Valid search parameters according to user field and filter configuration.
 */
export const validFilterParams = (
  params,
  userFieldsConfig,
  defaultFiltersConfig,
  listingCategories,
  dropNonFilterParams = true
) => {
  const userFieldFiltersConfig = userFieldsConfig.filter(
    config => config.filterConfig?.indexForSearch
  );

  const userFieldParamNames = userFieldFiltersConfig.map(f =>
    constructQueryParamName(f.key, f.scope)
  );
  const defaultFilterParamNames = defaultFiltersConfig.map(f =>
    constructQueryParamName(f.key, f.scope)
  );
  const filterParamNames = [...userFieldParamNames, ...defaultFilterParamNames];

  const unlimitedSearchParams = omitLimitedUserFieldParams(
    params,
    userFieldsConfig,
    defaultFiltersConfig,
    listingCategories
  );
  const paramEntries = Object.entries(unlimitedSearchParams);

  const userFieldsFilterParamNames = paramEntries.reduce((validParams, entry) => {
    const [paramName, paramValue] = entry;
    const isIndependentParam = filterParamNames.includes(paramName);

    return isIndependentParam
      ? {
          ...validParams,
          ...validURLParamForExtendedData(
            paramName,
            paramValue,
            userFieldFiltersConfig,
            defaultFiltersConfig
          ),
        }
      : dropNonFilterParams
      ? { ...validParams }
      : { ...validParams, [paramName]: paramValue };
  }, {});

  return { ...userFieldsFilterParamNames };
};

/**
 * Extracts valid search parameters from URL location object.
 * Filters parameters according to user field and default filter configurations.
 *
 * @param {Object} props - Object containing location and config properties.
 * @returns {Object} Validated search parameters from the URL query.
 */
export const validUrlQueryParamsFromProps = props => {
  const { location, config } = props;
  const { userFields: userFieldsConfig } = config?.user || {};
  const { defaultFilters: defaultFiltersConfig } = config?.search || {};
  const listingCategories = config.categoryConfiguration.categories;

  // eslint-disable-next-line no-unused-vars
  const searchInURL = parse(location.search);
  return validFilterParams(
    searchInURL,
    userFieldsConfig,
    defaultFiltersConfig,
    listingCategories,
    false
  );
};

/**
 * Computes initial values for Final Form based on current search filters.
 * Determines values from URL query or state, depending on live edit mode.
 *
 * @param {Object} props - Object containing location, userFieldsConfig, and defaultFiltersConfig.
 * @param {Object} currentQueryParams - Current state of query parameters.
 * @returns {Function} Function accepting queryParamNames and isLiveEdit, returning initial values.
 */
export const initialValues = (props, currentQueryParams) => (queryParamNames, isLiveEdit) => {
  const urlQueryParams = validUrlQueryParamsFromProps(props);

  // Get initial value for a given parameter from state if its there.
  const getInitialValue = paramName => {
    // Query parameters that are in state (user might have not yet clicked "Apply")
    const currentQueryParam = currentQueryParams[paramName];
    const hasQueryParamInState = typeof currentQueryParam !== 'undefined';
    return hasQueryParamInState && !isLiveEdit ? currentQueryParam : urlQueryParams[paramName];
  };

  // Return all the initial values related to given queryParamNames
  const isArray = Array.isArray(queryParamNames);
  return isArray
    ? queryParamNames.reduce((acc, paramName) => {
        const initValue = getInitialValue(paramName);
        const addInitialValueMaybe = initValue ? { [paramName]: initValue } : {};
        return { ...acc, ...addInitialValueMaybe };
      }, {})
    : {};
};

/**
 * Extracts and validates search parameters based on user field and default filter configurations.
 * Filters out parameters that are not relevant to the current search configuration.
 *
 * @param {Object} params - Search query parameters from the URL.
 * @param {Array<Object>} userFieldsConfig - Extended data configuration with indexForSearch === true.
 * @param {Array<Object>} defaultFiltersConfig - Default filter configurations for validation.
 * @returns {Object} Valid search parameters according to user field configuration.
 */
export const pickSearchParamsOnly = (
  params,
  userFieldsConfig,
  defaultFiltersConfig,
  listingCategories
) => {
  const filterParams = validFilterParams(
    params,
    userFieldsConfig,
    defaultFiltersConfig,
    listingCategories
  );
  return filterParams;
};

/**
 * Picks and synchronizes search parameters from the URL and application state.
 * Ensures consistency between URL search parameters and search state in props.
 *
 * @param {Object} searchFromLocation - Search parameters from URL (location.search).
 * @param {Object} searchParamsInProps - Search parameters from state/store.
 * @param {Array<Object>} userFieldsConfig - Configuration for user's extended data.
 * @param {Array<Object>} defaultFiltersConfig - Default filter configurations for validation.
 * @returns {Object} Contains:
 *   - searchParamsInURL: Search parameters from URL, excluding pagination-related params.
 *   - urlQueryParams: Validated and filtered search parameters.
 *   - searchParamsAreInSync: Boolean indicating if URL and state search params match.
 */
export const searchParamsPicker = (
  searchFromLocation,
  searchParamsInProps,
  userFieldsConfig,
  defaultFiltersConfig,
  listingCategories
) => {
  const { page, ...searchParamsInURL } = parse(searchFromLocation);

  // Pick only search params that are part of current search configuration
  const queryParamsFromSearchParams = pickSearchParamsOnly(
    searchParamsInProps,
    userFieldsConfig,
    defaultFiltersConfig,
    listingCategories
  );
  // Pick only search params that are part of current search configuration
  const queryParamsFromURL = pickSearchParamsOnly(
    searchParamsInURL,
    userFieldsConfig,
    defaultFiltersConfig,
    listingCategories
  );

  // Page transition might initially use values from previous search
  const searchParamsAreInSync =
    stringify(queryParamsFromURL) === stringify(queryParamsFromSearchParams);

  return {
    urlQueryParams: queryParamsFromURL,
    searchParamsInURL,
    searchParamsAreInSync,
  };
};

/**
 * Groups user field configurations into primary and secondary categories.
 * Only includes fields that are indexed for search and are marked as primary or secondary.
 *
 * @param {Array<Object>} configs - User extended data configurations.
 * @returns {Array<Array<Object>>} Array containing two subarrays:
 *   - Primary config array (index 0)
 *   - Secondary config array (index 1)
 */
export const groupUserFieldConfigs = configs =>
  configs.reduce(
    (grouped, config) => {
      const [primary, secondary] = grouped;
      const { filterConfig } = config;
      const isIndexed = filterConfig?.indexForSearch === true;
      const isPrimary = filterConfig?.group === 'primary';
      return isIndexed && isPrimary
        ? [[...primary, config], secondary]
        : isIndexed
        ? [primary, [...secondary, config]]
        : grouped;
    },
    [[], []]
  );

/**
 * Creates a schema.org-compatible JSON-LD schema for search results.
 *
 * @param {Array<Object>} users - List of users to include in the schema.
 * @param {Object} mainSearchData - Main search data (e.g., address, keywords).
 * @param {Object} intl - Internationalization object.
 * @param {Array<Object>} routeConfiguration - Route configuration for generating URLs.
 * @param {Object} config - Application configuration.
 * @returns {Object} Schema.org-compatible JSON-LD schema.
 */
export const createSearchResultSchema = (
  users,
  mainSearchData,
  intl,
  routeConfiguration,
  config
) => {
  const marketplaceName = config.marketplaceName;
  const { address, keywords } = mainSearchData;
  const keywordsMaybe = keywords ? `"${keywords}"` : null;
  const searchTitle =
    address || keywordsMaybe || intl.formatMessage({ id: 'SearchUsersPage.schemaForSearch' });
  const schemaDescription = intl.formatMessage({ id: 'SearchUsersPage.schemaDescription' });
  const schemaTitle = intl.formatMessage(
    { id: 'SearchUsersPage.schemaTitle' },
    { searchTitle, marketplaceName }
  );

  const schemaUsers = users.map((u, i) => {
    const displayName = userDisplayNameAsString(u);
    const pathToUser = createResourceLocatorString('ProfilePage', routeConfiguration, {
      id: u.id.uuid,
    });
    return {
      '@type': 'ListItem',
      position: i,
      url: `${config.marketplaceRootURL}${pathToUser}`,
      name: displayName,
    };
  });

  const schemaMainEntity = JSON.stringify({
    '@type': 'ItemList',
    name: searchTitle,
    itemListOrder: 'http://schema.org/ItemListOrderAscending',
    itemListElement: schemaUsers,
  });
  return {
    title: schemaTitle,
    description: schemaDescription,
    schema: {
      '@context': 'http://schema.org',
      '@type': 'SearchResultsPage',
      description: schemaDescription,
      name: schemaTitle,
      mainEntity: [schemaMainEntity],
    },
  };
};
